import { useEffect, useState } from 'react';
import { Fade, FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';
import { BarChart } from '@mui/x-charts';
import { useIntl } from 'react-intl';
import MDBox from 'components/MDBox';
import { useGetUsagesQuery } from 'services/usagesApi';
import { parseDate } from 'utils/formatTime';
import UsageLimits from 'pages/usages/limits';
// Import the UsageLimits component you want to add

const formatDate = (date) => {
  if (typeof date === 'string') {
    return date.split('T')[0];
  }
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  console.warn('formatDate received invalid date:', date);
  return '';
};

const getDaysBetween = (startDate, endDate) => {
  const dates = [];
  const currentDate = new Date(startDate);
  const lastDate = new Date(endDate);

  while (currentDate <= lastDate) {
    dates.push(formatDate(currentDate.toISOString()));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

const aggregateDataByAssistant = (data, startDate, endDate, assistantFilter = null) => {
  const allDays = getDaysBetween(startDate, endDate);
  const aggregated = {};

  allDays.forEach((day) => {
    aggregated[day] = { date: day, inputToken: 0, outputToken: 0 };
  });

  data.forEach(({ assitantCode, date, inputToken, outputToken }) => {
    if (assistantFilter && assitantCode !== assistantFilter) return;

    const formattedDate = formatDate(parseDate(date));

    if (!aggregated[formattedDate]) {
      aggregated[formattedDate] = { date: formattedDate, inputToken: 0, outputToken: 0 };
    }

    aggregated[formattedDate].inputToken += inputToken || 0;
    aggregated[formattedDate].outputToken += outputToken || 0;
  });

  return Object.values(aggregated);
};

const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0 = janvier

  const start = new Date(year, month, 1); // 1er jour du mois
  const end = new Date(year, month + 1, 0, 23, 59, 59); // dernier jour du mois

  const toSimpleDate = (date) => {
    if (!(date instanceof Date) || isNaN(date)) {
      console.warn('Invalid date:', date);
      return null;
    }
    return date.toISOString().split('T')[0];
  };

  return {
    start: toSimpleDate(start),
    end: toSimpleDate(end)
  };
};

export default function Usages() {
  const [startDate, setStartDate] = useState(getCurrentMonthDates().start);
  const [endDate, setEndDate] = useState(getCurrentMonthDates().end);
  const [selectedAssistant, setSelectedAssistant] = useState('');
  const { formatMessage: msg } = useIntl();
  const {
    data: usageData = [],
    isError,
    isLoading,
    error
  } = useGetUsagesQuery({ start: startDate, end: endDate });

  const assistants = [...new Set(usageData.map((usage) => usage.assitantCode))];

  const aggregatedData =
    usageData.length > 0
      ? aggregateDataByAssistant(usageData, startDate, endDate, selectedAssistant)
      : [];

  useEffect(() => {
    // Initial setup to ensure dates are correctly set if the component mounts later
    if (!startDate || !endDate) {
      const { start, end } = getCurrentMonthDates();
      setStartDate(start);
      setEndDate(end);
    }
  }, [endDate, startDate]); // Empty dependency array means this runs once on mount

  const handleAssistantChange = (event) => {
    setSelectedAssistant(event.target.value);
  };

  const handleStartDateChange = (event) => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = (event) => {
    setEndDate(event.target.value);
  };

  if (isError) {
    return <MDBox>Erreur lors du chargement des données: {error.toString()}</MDBox>;
  }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox display="flex" sx={{ height: '100%', width: '100%', gap: '16px' }}>
        <MDBox flex={1} display="flex" flexDirection="column">
          <Paper elevation={2} sx={{ p: 2, pt: 3, borderRadius: 0, overflow: 'hidden' }}>
            <MDBox display="flex" justifyContent="space-between">
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="assistant-select-label">
                  {msg({ id: 'page.usages.choose.assistant' })}
                </InputLabel>
                <Select
                  id="assistant-select"
                  value={selectedAssistant}
                  label={msg({ id: 'page.usages.choose.assistant' })}
                  onChange={handleAssistantChange}
                  variant="outlined"
                >
                  <MenuItem value="">Tous</MenuItem>
                  {assistants.map((assistant) => (
                    <MenuItem key={assistant} value={assistant}>
                      {assistant}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <MDBox display="flex" gap="1px">
                <TextField
                  type="date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  label={msg({ id: 'page.usages.start.date' })}
                />
                <TextField
                  type="date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  label={msg({ id: 'page.usages.end.date' })}
                />
              </MDBox>
            </MDBox>
          </Paper>
          <Paper elevation={2} sx={{ flex: 1, p: 2, borderRadius: 0, overflow: 'hidden' }}>
            <BarChart
              loading={isLoading}
              slotProps={{
                loadingOverlay: { message: msg({ id: 'page.usages.download.progress' }) },
                noDataOverlay: { message: msg({ id: 'page.usages.no.data' }) }
              }}
              dataset={aggregatedData}
              xAxis={[{ scaleType: 'band', dataKey: 'date' }]}
              series={[
                {
                  dataKey: 'inputToken',
                  label: msg({ id: 'page.usages.input.token' }),
                  color: '#8884d8',
                  barWidth: 20
                },
                {
                  dataKey: 'outputToken',
                  label: msg({ id: 'page.usages.output.token' }),
                  color: '#82ca9d',
                  barWidth: 20
                }
              ]}
            />
          </Paper>
        </MDBox>

        <MDBox flex={0.3} display="flex" flexDirection="column">
          <Paper elevation={2} sx={{ flex: 1, p: 2, overflow: 'auto' }}>
            <UsageLimits />
          </Paper>
        </MDBox>
      </MDBox>
    </Fade>
  );
}
